import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { first } from "lodash";
import moment from "moment";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";

import { fetchUpcomingEvents } from "../community-events/communityEventsSlice";
import { useUpcomingPastCommunityEvents } from "../community-events/hooks";
import { clearPageInterval, setPageInterval } from "../../utils";
import useInterval from "../../hooks/useInterval";
import {
  getWebinarPollIntervalMs,
  getWebinarStatusLabel,
  isWebinarUrgent,
  type WebinarStatusLabel,
} from "./webinarTiming";

const DISPLAY_TICK_MS = 30 * 1000;

type CommunityEventLike = {
  scheduled_time?: string;
  start_time?: string | null;
};

function minutesUntilScheduled(
  scheduledTime: string | undefined,
): number | null {
  if (!scheduledTime) {
    return null;
  }
  return moment(scheduledTime).diff(moment(), "minute");
}

export function useUpcomingWebinar(): {
  showUrgencyBadge: boolean;
  webinarStatusLabel: WebinarStatusLabel | null;
} {
  const dispatch = useDispatch<ThunkDispatch<unknown, undefined, AnyAction>>();
  const [upcoming] = useUpcomingPastCommunityEvents();
  const nextEvent = first(upcoming) as CommunityEventLike | undefined;
  const inProgress = Boolean(nextEvent?.start_time);
  const scheduledTime = nextEvent?.scheduled_time;

  // Re-render periodically so bucket/urgency advance between network polls.
  const [, setTick] = useState(0);
  useInterval(() => {
    setTick((n) => n + 1);
  }, DISPLAY_TICK_MS);

  useEffect(() => {
    dispatch(fetchUpcomingEvents());
  }, [dispatch]);

  useEffect(() => {
    const minutesUntil = minutesUntilScheduled(scheduledTime);
    const intervalMs = getWebinarPollIntervalMs(
      minutesUntil === null ? 24 * 60 : minutesUntil,
    );
    const intervalId = setPageInterval(() => {
      dispatch(fetchUpcomingEvents());
    }, intervalMs);

    return () => {
      clearPageInterval(intervalId);
    };
  }, [dispatch, scheduledTime]);

  const minutesUntil = minutesUntilScheduled(scheduledTime);

  return {
    showUrgencyBadge: isWebinarUrgent(inProgress, minutesUntil),
    webinarStatusLabel: nextEvent
      ? getWebinarStatusLabel(inProgress, minutesUntil)
      : null,
  };
}
